import React, { useState } from 'react';
import integrationService from '../../services/api/integrationService';

export const financialPermissions = [
  ['financial:salary:read', 'Read salary terms'], ['financial:salary:write', 'Publish salary terms'],
  ['financial:billing:read', 'Read billing terms'], ['financial:billing:write', 'Publish billing terms'],
];
export const parseFinancialAccess = (sourceSystems, employerCvrs) => ({
  sourceSystems: sourceSystems.split(/[,\s]+/).map((value) => value.trim()).filter(Boolean),
  employerCvrs: employerCvrs.split(/[,\s]+/).map((value) => value.trim()).filter(Boolean),
});
export const FinancialAccessFields = ({ prefix, sources, employers, setSources, setEmployers, disabled }) => (
  <div style={{ display: 'grid', gap: '.5rem', margin: '.7rem 0' }}>
    <label htmlFor={`${prefix}-sources`}>Allowed source systems (comma separated)
      <input id={`${prefix}-sources`} value={sources} onChange={(e) => setSources(e.target.value)} disabled={disabled} placeholder="vikarcrm" />
    </label>
    <label htmlFor={`${prefix}-employers`}>Allowed payroll employer CVRs (comma separated)
      <input id={`${prefix}-employers`} value={employers} onChange={(e) => setEmployers(e.target.value)} disabled={disabled} />
    </label>
    <small>Choose the legal payroll employers this key may access. Customer CVRs do not grant payroll access.</small>
  </div>
);

export default function FinancialAccessEditor({ client, onSaved }) {
  const evaClient = (client.scopes || []).some((scope) => scope.startsWith('eva:'));
  const [statusRead, setStatusRead] = useState((client.scopes || []).includes('integration:status:read'));
  const [bindingWrite, setBindingWrite] = useState((client.scopes || []).includes('employment:binding:write'));
  const [commandsRetire, setCommandsRetire] = useState((client.scopes || []).includes('employment:commands:retire'));
  const [open, setOpen] = useState(false);
  const [scopes, setScopes] = useState([]);
  const [sources, setSources] = useState('');
  const [employers, setEmployers] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const show = () => {
    setScopes((client.scopes || []).filter((scope) => scope.startsWith('financial:')));
    setStatusRead((client.scopes || []).includes('integration:status:read'));
    setBindingWrite((client.scopes || []).includes('employment:binding:write'));
    setCommandsRetire((client.scopes || []).includes('employment:commands:retire'));
    setSources((client.financialAccess?.sourceSystems || []).join(', '));
    setEmployers((client.financialAccess?.employerCvrs || []).join(', '));
    setError(''); setOpen(true);
  };
  const save = async () => {
    setBusy(true); setError('');
    try {
      // Eva edits only its allowlists. Adding legacy financial scopes would
      // authorize a different API audience and is also rejected by the server.
      await integrationService.updateFinancialAccess(client.id, { financialScopes: evaClient ? [] : scopes,
        ...(!evaClient ? { statusRead, bindingWrite, commandsRetire } : {}),
        financialAccess: evaClient || statusRead || bindingWrite || commandsRetire || scopes.length ? parseFinancialAccess(sources, employers) : null,
        expectedUpdatedAt: client.updatedAt });
      await onSaved(); setOpen(false);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  if (!open) return <button type="button" onClick={show}>Financial access</button>;
  return <fieldset style={{ minWidth: 240 }}>
    <legend>Financial access — {client.name}</legend>
    {!evaClient && <label style={{ display: 'block' }}>
      <input type="checkbox" disabled={busy} checked={statusRead} onChange={event => setStatusRead(event.target.checked)} /> Read integration and employment status
    </label>}
    {!evaClient && <label style={{ display: 'block' }}>
      <input type="checkbox" disabled={busy} checked={bindingWrite} onChange={event => setBindingWrite(event.target.checked)} /> Record reviewed legal-employer binding (also requires status read)
    </label>}
    {!evaClient && <label style={{ display: 'block' }}>
      <input type="checkbox" disabled={busy} checked={commandsRetire} onChange={event => setCommandsRetire(event.target.checked)} /> Retire reviewed employment commands (also requires status read)
    </label>}
    {!evaClient && financialPermissions.map(([value, label]) => <label key={value} style={{ display: 'block' }}>
      <input type="checkbox" disabled={busy} checked={scopes.includes(value)}
        onChange={(e) => setScopes((old) => e.target.checked ? [...old, value] : old.filter((scope) => scope !== value))} /> {label}
    </label>)}
    <FinancialAccessFields prefix={`financial-${client.id}`} sources={sources} employers={employers}
      setSources={setSources} setEmployers={setEmployers} disabled={busy} />
    {error && <p role="alert">{error}</p>}
    <button type="button" disabled={busy} onClick={save}>Save financial access</button>{' '}
    <button type="button" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
  </fieldset>;
}
